import {
  Users,
  RefreshCw,
  Laptop,
  KeyRound,
  SlidersHorizontal,
  LayoutDashboard,
  LayoutGrid,
  FileText,
  ShieldCheck,
  Network,
  Sparkles,
  Settings2,
  Brain,
  Zap,
  Plug,
  Database,
  UserCog,
} from "lucide-react";
import type { NavSection } from "@skypointcloud/skypoint-ui";

export const WORKFORCE_NAV_IDS = {
  DASHBOARDS: "wf-dashboards",
  PEOPLE: "wf-people",
  ORG: "org",
  LIFECYCLE: "wf-lifecycle",
  ACCESS: "wf-access",
  APPLICATIONS: "wf-applications",
  ASSETS: "wf-assets",
  LICENSES: "wf-licenses",
  REPORTS: "wf-reports",
  SETTINGS: "wf-settings",
  /** Users & access — the shared /users page (shared user management). */
  USERS: "wf-users",
  /**
   * skyAgent and skyBrain reuse the SERVICE-DESK routes rather than getting
   * workforce copies: one agent, one conversation history, one set of settings.
   * Only the nav ids are module-local, so each sidebar can highlight its own
   * row.
   */
  SKYAGENT: "wf-skyagent",
  SKYAGENT_CONFIGURATION: "wf-skyagent-configuration",
  SKYBRAIN_SYSTEM_PROMPT: "wf-skybrain-system-prompt",
  SKYBRAIN_MEMORY: "wf-skybrain-memory",
  SKYBRAIN_SKILLS: "wf-skybrain-skills",
  SKYBRAIN_INTEGRATIONS: "wf-skybrain-integrations",
  SKYBRAIN_INDEX: "wf-skybrain-index",
  SKYBRAIN_SETTINGS: "wf-skybrain-settings",
} as const;

export type WorkforceNavId = (typeof WORKFORCE_NAV_IDS)[keyof typeof WORKFORCE_NAV_IDS];

/**
 * Left-nav for the WLM module.
 *
 * The ten workforce destinations stay one flat headerless list (Slice A of the
 * redesign): People, Access and Assets are tabbed pages, Applications is a
 * standalone page, Reports is the compliance hub. The whole module sits behind
 * useWorkforceGate (admin-only).
 *
 * skyAgent and skyBrain follow in their own headed sections, matching the
 * Service Desk sidebar exactly — same order, same labels. They are not
 * workforce features, but the agent reaches workforce data now, and a person
 * who switched product should not have to switch back to ask it a question.
 * Both sections point at the shared routes and carry `from=workforce`, so the
 * shell keeps this module rather than flipping to Service Desk mid-task.
 */
export function buildWorkforceNavSections(): NavSection[] {
  return [
    {
      items: [
        { id: WORKFORCE_NAV_IDS.DASHBOARDS, label: "Dashboard", icon: LayoutDashboard },
        { id: WORKFORCE_NAV_IDS.PEOPLE, label: "People", icon: Users },
        { id: WORKFORCE_NAV_IDS.ORG, label: "Org chart", icon: Network },
        { id: WORKFORCE_NAV_IDS.LIFECYCLE, label: "Lifecycle", icon: RefreshCw },
        { id: WORKFORCE_NAV_IDS.ACCESS, label: "Access", icon: ShieldCheck },
        { id: WORKFORCE_NAV_IDS.APPLICATIONS, label: "Applications", icon: LayoutGrid },
        { id: WORKFORCE_NAV_IDS.ASSETS, label: "Assets", icon: Laptop },
        { id: WORKFORCE_NAV_IDS.LICENSES, label: "Licenses", icon: KeyRound },
        { id: WORKFORCE_NAV_IDS.REPORTS, label: "Reports", icon: FileText },
        { id: WORKFORCE_NAV_IDS.SETTINGS, label: "Settings", icon: SlidersHorizontal },
        { id: WORKFORCE_NAV_IDS.USERS, label: "Users & access", icon: UserCog },
      ],
    },
    {
      header: "skyAgent",
      items: [
        { id: WORKFORCE_NAV_IDS.SKYAGENT, label: "Workspace", icon: Sparkles },
        { id: WORKFORCE_NAV_IDS.SKYAGENT_CONFIGURATION, label: "Configuration", icon: Settings2 },
      ],
    },
    {
      header: "skyBrain",
      items: [
        { id: WORKFORCE_NAV_IDS.SKYBRAIN_SYSTEM_PROMPT, label: "Instructions", icon: FileText },
        { id: WORKFORCE_NAV_IDS.SKYBRAIN_MEMORY, label: "Memory", icon: Brain },
        { id: WORKFORCE_NAV_IDS.SKYBRAIN_SKILLS, label: "Skills", icon: Zap },
        { id: WORKFORCE_NAV_IDS.SKYBRAIN_INTEGRATIONS, label: "Integrations", icon: Plug },
        { id: WORKFORCE_NAV_IDS.SKYBRAIN_INDEX, label: "Index", icon: Database },
        { id: WORKFORCE_NAV_IDS.SKYBRAIN_SETTINGS, label: "Settings", icon: SlidersHorizontal },
      ],
    },
  ];
}
